import { useEffect, useState } from "react";
import {
    FaPlus,
    FaEdit,
    FaTrash,
    FaSearch,
    FaSpinner,
    FaArrowLeft,
    FaArrowUp,
    FaArrowDown,
    FaTimes
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { getMenuPermissions } from "../utils/auth";

import "./AirportServices.css";

// Requires: npm install react-toastify

const API_URL = process.env.REACT_APP_API_URL;


function AirportServices() {

    // Mirrors require_menu_permission("airport_services") on the
    // backend - hides a button when the click behind it would 403.
    const perms = getMenuPermissions("airport_services");

    const emptyChip = (order) => ({
        id: 0,
        chip_label: "",
        chip_value: "",
        highlight: false,
        chip_order: order
    });

    const emptyForm = {
        id: "",
        icon: "",
        label: "",
        description: "",
        is_active: true,
        chips: []
    };

    const [services, setServices] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [search, setSearch] = useState("");

    // Inline form -- shown instead of the table, same approach as
    // EmergencyContacts (no modal/overlay).
    const [showForm, setShowForm] = useState(false);
    const [isEdit, setIsEdit] = useState(false);

    const [form, setForm] = useState(emptyForm);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        loadServices();
    }, []);

    useEffect(() => {

        const value = search.toLowerCase();

        setFiltered(

            services.filter(item =>
                item.label.toLowerCase().includes(value) ||
                item.description.toLowerCase().includes(value) ||
                item.chips.some(chip =>
                    chip.chip_label.toLowerCase().includes(value) ||
                    chip.chip_value.toLowerCase().includes(value)
                )
            )

        );

    }, [search, services]);

    const loadServices = async () => {

        setIsLoading(true);

        try {

            const res = await fetch(`${API_URL}/portal/airport-services`);

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            if (Array.isArray(data)) {

                setServices(data);

            } else if (data.status) {

                setServices(data.data || []);

            } else {

                setServices([]);

                toast.error(data.message || "Failed to load airport services");

            }

        } catch (err) {

            console.error(err);

            setServices([]);

            toast.error("Unable to load airport services. Please try again.");

        } finally {

            setIsLoading(false);

        }

    };

    // -----------------------------------------
    // Header field handlers
    // -----------------------------------------

    const handleChange = (e) => {

        setForm({
            ...form,
            [e.target.name]: e.target.value
        });

    };

    const handleActiveToggle = () => {

        setForm({
            ...form,
            is_active: !form.is_active
        });

    };

    // -----------------------------------------
    // Chip handlers
    // -----------------------------------------

    const handleChipChange = (index, field, value) => {

        const updated = form.chips.map((chip, i) =>
            i === index
                ? { ...chip, [field]: value }
                : chip
        );

        setForm({
            ...form,
            chips: updated
        });

    };

    const addChip = () => {

        setForm({
            ...form,
            chips: [
                ...form.chips,
                emptyChip(form.chips.length)
            ]
        });

    };

    const removeChip = (index) => {

        const updated = form.chips
            .filter((_, i) => i !== index)
            .map((chip, i) => ({
                ...chip,
                chip_order: i
            }));

        setForm({
            ...form,
            chips: updated
        });

    };

    const moveChip = (index, direction) => {

        const target = index + direction;

        if (target < 0 || target >= form.chips.length) return;

        const updated = [...form.chips];

        [updated[index], updated[target]] = [updated[target], updated[index]];

        setForm({
            ...form,
            chips: updated.map((chip, i) => ({
                ...chip,
                chip_order: i
            }))
        });

    };

    // -----------------------------------------
    // Open / close form
    // -----------------------------------------

    const openAdd = () => {

        setIsEdit(false);

        setForm(emptyForm);

        setShowForm(true);

    };

    const openEdit = (item) => {

        setIsEdit(true);

        setForm({
            ...item,
            chips: item.chips.map(chip => ({ ...chip }))
        });

        setShowForm(true);

    };

    const closeForm = () => {

        if (isSaving) return; // don't let them leave mid-save

        setShowForm(false);

        setForm(emptyForm);

    };

    // -----------------------------------------
    // Save
    // -----------------------------------------

    const saveService = async () => {

        if (
            !form.icon ||
            !form.label ||
            !form.description
        ) {

            toast.warn("Please fill icon, label and description");
            return;

        }

        for (const chip of form.chips) {

            if (!chip.chip_label || !chip.chip_value) {

                toast.warn("Please fill all chip fields, or remove empty chips");
                return;

            }

        }

        setIsSaving(true);

        try {

            const payload = {

                ...form,

                id: isEdit ? form.id : 0,

                chips: form.chips.map((chip, i) => ({
                    ...chip,
                    chip_order: i
                }))

            };

            const res = await fetch(`${API_URL}/portal/airport-services`, {

                method: isEdit ? "PUT" : "POST",

                headers: {
                    "Content-Type": "application/json"
                },

                body: JSON.stringify(payload)

            });

            const data = await res.json();

            if (res.ok && data.status) {

                setShowForm(false);

                setForm(emptyForm);

                toast.success(
                    isEdit
                        ? "Airport service updated successfully"
                        : "Airport service added successfully"
                );

                loadServices();

            } else {

                toast.error(data.message || "Failed to save airport service");

            }

        } catch (err) {

            console.error("Failed to save airport service:", err);

            toast.error("Something went wrong while saving the service");

        } finally {

            setIsSaving(false);

        }

    };

    // -----------------------------------------
    // Delete
    // -----------------------------------------

    const deleteService = async (id) => {

        const ok = window.confirm(
            "Are you sure you want to delete this service? This will also remove its chips."
        );

        if (!ok) return;

        setDeletingId(id);

        try {

            const res = await fetch(`${API_URL}/portal/airport-services/${id}`, {

                method: "DELETE"

            });

            const data = await res.json();

            if (res.ok && data.status) {

                toast.success("Airport service deleted successfully");

                loadServices();

            } else {

                toast.error(data.message || "Failed to delete airport service");

            }

        } catch (err) {

            console.error("Failed to delete airport service:", err);

            toast.error("Something went wrong while deleting the service");

        } finally {

            setDeletingId(null);

        }

    };

    return (

        <div className="page">

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
            />

            <div className="page-header">

                <h2>Airport Services</h2>

                {

                    !showForm && perms.add &&

                    <button
                        type="button"
                        className="add-btn"
                        onClick={openAdd}
                    >

                        <FaPlus />

                        Add Service

                    </button>

                }

            </div>

            {

                showForm ? (

                    // ===========================================
                    // INLINE FORM -- header fields + repeatable chips
                    // ===========================================
                    <div className="inline-form-container">

                        <div className="inline-form-header">

                            <button
                                type="button"
                                className="back-btn"
                                onClick={closeForm}
                                disabled={isSaving}
                            >

                                <FaArrowLeft />
                                Back to list

                            </button>

                            <h3>
                                {isEdit ? "Update Service" : "Add Service"}
                            </h3>

                        </div>

                        {/* ---------------- Header fields ---------------- */}

                        <div className="form-section-title">Service Details</div>

                        <div className="inline-form-grid">

                            <input
                                name="icon"
                                placeholder="Icon (emoji, e.g. 🏧)"
                                value={form.icon}
                                onChange={handleChange}
                                disabled={isSaving}
                                maxLength={4}
                            />

                            <input
                                name="label"
                                placeholder="Label (e.g. ATMs)"
                                value={form.label}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="description"
                                placeholder="Description"
                                value={form.description}
                                onChange={handleChange}
                                disabled={isSaving}
                                className="span-2"
                            />

                            <label className="toggle-row">

                                <span
                                    className={`toggle-switch ${form.is_active ? "on" : ""}`}
                                    onClick={() => !isSaving && handleActiveToggle()}
                                >
                                    <span className="toggle-knob" />
                                </span>

                                <span>
                                    {form.is_active ? "Active" : "Inactive"}
                                </span>

                            </label>

                        </div>

                        {/* ---------------- Chips section ---------------- */}

                        <div className="form-section-title chips-title">

                            <span>Chips</span>

                            <button
                                type="button"
                                className="add-chip-btn"
                                onClick={addChip}
                                disabled={isSaving}
                            >
                                <FaPlus /> Add Chip
                            </button>

                        </div>

                        {

                            form.chips.length === 0 ?

                                <div className="no-chips">
                                    No chips yet. Chips let you show extra details
                                    like locations under this service.
                                </div>

                                :

                                <div className="chip-list">

                                    {

                                        form.chips.map((chip, index) => (

                                            <div className="chip-row" key={index}>

                                                <div className="chip-order-controls">

                                                    <button
                                                        type="button"
                                                        className="chip-move-btn"
                                                        onClick={() => moveChip(index, -1)}
                                                        disabled={isSaving || index === 0}
                                                        title="Move up"
                                                    >
                                                        <FaArrowUp />
                                                    </button>

                                                    <span className="chip-index">{index + 1}</span>

                                                    <button
                                                        type="button"
                                                        className="chip-move-btn"
                                                        onClick={() => moveChip(index, 1)}
                                                        disabled={isSaving || index === form.chips.length - 1}
                                                        title="Move down"
                                                    >
                                                        <FaArrowDown />
                                                    </button>

                                                </div>

                                                <input
                                                    placeholder="Chip Label (e.g. LOCATION)"
                                                    value={chip.chip_label}
                                                    onChange={(e) => handleChipChange(index, "chip_label", e.target.value)}
                                                    disabled={isSaving}
                                                />

                                                <input
                                                    placeholder="Chip Value (e.g. Main Hall · Pillar 12)"
                                                    value={chip.chip_value}
                                                    onChange={(e) => handleChipChange(index, "chip_value", e.target.value)}
                                                    disabled={isSaving}
                                                />

                                                <label className="chip-highlight">

                                                    <input
                                                        type="checkbox"
                                                        checked={chip.highlight}
                                                        onChange={(e) => handleChipChange(index, "highlight", e.target.checked)}
                                                        disabled={isSaving}
                                                    />

                                                    Highlight

                                                </label>

                                                <button
                                                    type="button"
                                                    className="icon-btn delete-btn chip-remove-btn"
                                                    onClick={() => removeChip(index)}
                                                    disabled={isSaving}
                                                    title="Remove chip"
                                                >
                                                    <FaTimes />
                                                </button>

                                            </div>

                                        ))

                                    }

                                </div>

                        }

                        <div className="inline-form-actions">

                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={closeForm}
                                disabled={isSaving}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                className="save-btn"
                                onClick={saveService}
                                disabled={isSaving}
                            >

                                {

                                    isSaving

                                        ? <><FaSpinner className="spin" /> Saving...</>

                                        : isEdit
                                            ? "Update Service"
                                            : "Save Service"

                                }

                            </button>

                        </div>

                    </div>

                ) : (

                    // ===========================================
                    // TABLE VIEW
                    // ===========================================
                    <>

                        <div className="search-box">

                            <FaSearch />

                            <input
                                placeholder="Search by label, description, chips..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />

                        </div>

                        <div className="table-container">

                            <table>

                                <thead>

                                    <tr>

                                        <th style={{ width: "60px" }}>Icon</th>
                                        <th>Label</th>
                                        <th>Description</th>
                                        <th>Chips</th>
                                        <th style={{ width: "90px" }}>Status</th>
                                        <th style={{ width: "120px" }}>Action</th>

                                    </tr>

                                </thead>

                                <tbody>

                                    {

                                        isLoading ?

                                            <tr>

                                                <td colSpan="6" style={{ textAlign: "center", padding: "40px" }}>
                                                    <FaSpinner className="spin" /> Loading services...
                                                </td>

                                            </tr>

                                            :

                                            filtered.length === 0 ?

                                                <tr>

                                                    <td colSpan="6" style={{ textAlign: "center", padding: "40px" }}>
                                                        No Records Found
                                                    </td>

                                                </tr>

                                                :

                                                filtered.map(item => (

                                                    <tr key={item.id}>

                                                        <td className="icon-cell">{item.icon}</td>

                                                        <td>{item.label}</td>

                                                        <td className="desc-cell">{item.description}</td>

                                                        <td>

                                                            {

                                                                item.chips.length === 0 ?

                                                                    <span className="no-chip-text">—</span>

                                                                    :

                                                                    <div className="chip-preview">

                                                                        {

                                                                            item.chips.map(chip => (

                                                                                <span
                                                                                    key={chip.id}
                                                                                    className={`chip-pill ${chip.highlight ? "highlight" : ""}`}
                                                                                >
                                                                                    {chip.chip_label}: {chip.chip_value}
                                                                                </span>

                                                                            ))

                                                                        }

                                                                    </div>

                                                            }

                                                        </td>

                                                        <td>

                                                            <span className={`status-badge ${item.is_active ? "active" : "inactive"}`}>
                                                                {item.is_active ? "Active" : "Inactive"}
                                                            </span>

                                                        </td>

                                                        <td>

                                                            <div className="action-buttons">

                                                                {perms.edit &&
                                                                <button
                                                                    type="button"
                                                                    className="icon-btn edit-btn"
                                                                    title="Edit service"
                                                                    aria-label="Edit service"
                                                                    onClick={() => openEdit(item)}
                                                                    disabled={deletingId === item.id}
                                                                >
                                                                    <FaEdit />
                                                                </button>
                                                                }

                                                                {perms.delete &&
                                                                <button
                                                                    type="button"
                                                                    className="icon-btn delete-btn"
                                                                    title="Delete service"
                                                                    aria-label="Delete service"
                                                                    onClick={() => deleteService(item.id)}
                                                                    disabled={deletingId === item.id}
                                                                >

                                                                    {

                                                                        deletingId === item.id

                                                                            ? <FaSpinner className="spin" />

                                                                            : <FaTrash />

                                                                    }

                                                                </button>
                                                                }

                                                            </div>

                                                        </td>

                                                    </tr>

                                                ))

                                    }

                                </tbody>

                            </table>

                        </div>

                    </>

                )

            }

        </div>

    );

}

export default AirportServices;